import React, { useState, useEffect, useCallback } from 'react';
import { Badge } from './ui/badge';
import { ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react';
import { mockData } from '../mock';

const PortfolioSlideshow = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const projects = mockData.portfolio;

  const nextSlide = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % projects.length);
  }, [projects.length]);

  const prevSlide = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + projects.length) % projects.length);
  }, [projects.length]);

  const goToSlide = (index) => {
    setCurrentIndex(index);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  // Auto-play
  useEffect(() => {
    if (!isAutoPlaying) return;
    const interval = setInterval(nextSlide, 6000);
    return () => clearInterval(interval);
  }, [isAutoPlaying, nextSlide]);

  const currentProject = projects[currentIndex];

  return (
    <div className="relative w-full">
      {/* Main Card Container */}
      <div 
        className="relative bg-white rounded-3xl shadow-2xl overflow-hidden mx-auto max-w-6xl"
        onMouseEnter={() => setIsAutoPlaying(false)}
        onMouseLeave={() => setIsAutoPlaying(true)}
      >
        {/* Desktop Layout */}
        <div className="hidden lg:block">
          <div className="relative aspect-[16/9] overflow-hidden">
            {/* Full Image Background */}
            <img 
              key={currentIndex}
              src={currentProject.image}
              alt={currentProject.title}
              className="w-full h-full object-cover transition-all duration-700 ease-out hover:scale-105"
            />
            
            {/* Subtle gradient overlay at bottom for text readability */}
            <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
            
            {/* Content Overlay */}
            <div className="absolute inset-x-0 bottom-0 p-8 lg:p-12">
              <div className="max-w-3xl">
                {/* Tags */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {currentProject.tags.map((tag, idx) => (
                    <Badge 
                      key={idx} 
                      className="bg-white/20 backdrop-blur-sm text-white border-white/30 hover:bg-white/30 transition-colors"
                    >
                      {tag}
                    </Badge>
                  ))}
                </div>

                {/* Title */}
                <h3 className="text-3xl lg:text-4xl font-bold text-white mb-3 drop-shadow-lg">
                  {currentProject.title}
                </h3>

                {/* Description */}
                <p className="text-white/90 text-lg leading-relaxed mb-6 max-w-2xl drop-shadow">
                  {currentProject.description}
                </p>

                {/* CTA Button */}
                <a 
                  href={currentProject.link} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-white text-gray-900 font-semibold rounded-full transition-all hover:scale-105 hover:shadow-xl hover:bg-orange-50 group"
                >
                  Projekt ansehen
                  <ExternalLink size={18} className="group-hover:translate-x-1 transition-transform" />
                </a>
              </div>
            </div>

            {/* Navigation Arrows */}
            <button 
              onClick={prevSlide}
              className="absolute left-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 flex items-center justify-center rounded-full bg-white/90 hover:bg-white text-gray-800 transition-all hover:scale-110 shadow-lg"
              aria-label="Vorheriges Projekt"
            >
              <ChevronLeft size={24} />
            </button>
            <button 
              onClick={nextSlide}
              className="absolute right-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 flex items-center justify-center rounded-full bg-white/90 hover:bg-white text-gray-800 transition-all hover:scale-110 shadow-lg"
              aria-label="Nächstes Projekt"
            >
              <ChevronRight size={24} />
            </button>

            {/* Project Counter */}
            <div className="absolute top-6 right-6 px-4 py-2 bg-white/90 backdrop-blur-sm rounded-full text-gray-800 font-semibold shadow-lg">
              {currentIndex + 1} / {projects.length}
            </div>
          </div>
        </div>

        {/* Mobile Layout */}
        <div className="lg:hidden">
          {/* Image Section - Full width on mobile */}
          <div className="relative aspect-[4/3] overflow-hidden">
            <img 
              key={currentIndex}
              src={currentProject.image}
              alt={currentProject.title}
              className="w-full h-full object-cover"
            />
            
            {/* Navigation Arrows on Image */}
            <button 
              onClick={prevSlide}
              className="absolute left-3 top-1/2 -translate-y-1/2 z-20 w-10 h-10 flex items-center justify-center rounded-full bg-white/90 text-gray-800 shadow-lg"
              aria-label="Vorheriges Projekt"
            >
              <ChevronLeft size={20} />
            </button>
            <button 
              onClick={nextSlide}
              className="absolute right-3 top-1/2 -translate-y-1/2 z-20 w-10 h-10 flex items-center justify-center rounded-full bg-white/90 text-gray-800 shadow-lg"
              aria-label="Nächstes Projekt"
            >
              <ChevronRight size={20} />
            </button>

            {/* Counter Badge */}
            <div className="absolute top-4 right-4 px-3 py-1 bg-white/90 backdrop-blur-sm rounded-full text-sm text-gray-800 font-medium shadow">
              {currentIndex + 1} / {projects.length}
            </div>
          </div>

          {/* Content Section */}
          <div className="p-6 bg-white">
            {/* Tags */}
            <div className="flex flex-wrap gap-2 mb-3">
              {currentProject.tags.map((tag, idx) => (
                <Badge 
                  key={idx} 
                  className="bg-orange-100 text-orange-700 border-orange-200"
                >
                  {tag}
                </Badge>
              ))}
            </div>

            {/* Title */}
            <h3 className="text-2xl font-bold text-gray-900 mb-3">
              {currentProject.title}
            </h3>

            {/* Description */}
            <p className="text-gray-600 leading-relaxed mb-5">
              {currentProject.description}
            </p>

            {/* CTA Button */}
            <a 
              href={currentProject.link} 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-orange-500 to-red-500 text-white font-semibold rounded-full transition-all hover:shadow-lg"
            >
              Projekt ansehen
              <ExternalLink size={16} />
            </a>
          </div>
        </div>
      </div>

      {/* Dots Navigation */}
      <div className="flex justify-center gap-3 mt-8">
        {projects.map((project, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className="group relative"
            aria-label={`Zu ${project.title} wechseln`}
          >
            <span 
              className={`block h-2 rounded-full transition-all duration-300 ${
                index === currentIndex 
                  ? 'w-8 bg-gradient-to-r from-orange-500 to-red-500' 
                  : 'w-2 bg-gray-300 hover:bg-orange-300'
              }`}
            />
          </button>
        ))}
      </div>

      {/* Thumbnail Preview - Desktop Only */}
      <div className="hidden lg:flex justify-center gap-4 mt-8">
        {projects.map((project, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`relative overflow-hidden rounded-xl transition-all duration-300 ${
              index === currentIndex 
                ? 'ring-3 ring-orange-500 ring-offset-2 scale-105 shadow-lg' 
                : 'opacity-60 hover:opacity-100 grayscale hover:grayscale-0'
            }`}
          >
            <img 
              src={project.image} 
              alt={project.title}
              className="w-24 h-16 object-cover"
            />
            {index === currentIndex && (
              <div className="absolute inset-0 border-2 border-orange-500 rounded-xl"></div>
            )}
          </button>
        ))}
      </div>
    </div>
  );
};

export default PortfolioSlideshow;
